import { defineMessages } from 'react-intl-next';
import type { IntlShape } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

const i18n = defineMessages({
	errorTitle: {
		id: 'editor-offline-storage.version-insertion-error-flag.title',
		defaultMessage: 'Failed to save version',
		description: 'Title for the error flag when version insertion fails',
	},
	errorDescription: {
		id: 'editor-offline-storage.version-insertion-error-flag.description',
		defaultMessage:
			'An error occurred while backing up your offline edits. Your content is preserved and you can try again.',
		description:
			'Description for the error flag when version insertion fails initially and offline edits are not backed up',
	},
	errorDescriptionRetry: {
		id: 'editor-offline-storage.version-insertion-error-flag.description-retry',
		defaultMessage: "We're still having trouble backing up your offline edits. Please try again.",
		description:
			'Description for the error flag when version insertion fails after the user manually retries',
	},
	retryButton: {
		id: 'editor-offline-storage.version-insertion-error-flag.retry-button',
		defaultMessage: 'Retry',
		description: 'Button to retry backing up the offline edits',
	},
	successTitle: {
		id: 'editor-offline-storage.version-insertion-success-flag.title',
		defaultMessage: 'Backup saved',
		description:
			'Title for the success flag when version insertion succeeds after the user manually retries',
	},
	successDescription: {
		id: 'editor-offline-storage.version-insertion-success-flag.description',
		defaultMessage: 'Your offline edits have been backed up successfully.',
		description:
			'Description for the success flag when version insertion succeeds after the user manually retries',
	},
});

const ERROR_FLAG_ID = 'version-insertion-error-flag';
const SUCCESS_FLAG_ID = 'version-insertion-success-flag';

export const showVersionInsertionErrorFlag = (
	flags: FlagsStateContainer | undefined,
	intl: IntlShape,
	onRetry: () => void,
	onClose: () => void,
	manualRetryCount: number,
): void => {
	if (!flags) {
		return;
	}
	void flags.showWarningFlag({
		id: ERROR_FLAG_ID,
		title: intl.formatMessage(i18n.errorTitle),
		description: intl.formatMessage(
			manualRetryCount > 0 ? i18n.errorDescriptionRetry : i18n.errorDescription,
		),
		actions: [
			{
				content: intl.formatMessage(i18n.retryButton),
				onClick: onRetry,
			},
		],
		onClose,
	});
};

export const showVersionInsertionSuccessFlag = (
	flags: FlagsStateContainer | undefined,
	intl: IntlShape,
): void => {
	if (!flags) {
		return;
	}
	void flags.showSuccessFlag({
		id: SUCCESS_FLAG_ID,
		title: intl.formatMessage(i18n.successTitle),
		description: intl.formatMessage(i18n.successDescription),
	});
};

export const hideVersionInsertionErrorFlag = (flags: FlagsStateContainer | undefined): void => {
	if (!flags) {
		return;
	}
	void flags.hideFlag(ERROR_FLAG_ID);
};
